import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Loading, Empty, vc, formatNum } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wallet as WalletIcon, Coins, TrendingUp, Gift, ArrowDownToLine, Clock, Plus, ShieldCheck, Download, Star } from "lucide-react";
import { toast } from "sonner";

export default function Wallet() {
  const { user } = useAuth();
  const location = useLocation();
  const [w, setW] = useState(null);
  const [tab, setTab] = useState(location.state?.tab || "transactions");
  const [txns, setTxns] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [tips, setTips] = useState(null);
  const [withdrawals, setWithdrawals] = useState(null);
  const [packs, setPacks] = useState([]);
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");
  const [method, setMethod] = useState("PayPal");
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const minVc = user?.plan === "plus" ? 50 : 100;
  const isPlus = user?.plan === "plus";

  const loadWallet = () => api.get("/wallet").then(({ data }) => setW(data));
  useEffect(() => { loadWallet(); api.get("/payments/coins/packs").then(({ data }) => setPacks(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "transactions" && !txns) api.get("/wallet/transactions").then(({ data }) => setTxns(data));
    if (tab === "rewards" && !rewards) api.get("/wallet/rewards").then(({ data }) => setRewards(data));
    if (tab === "tips" && !tips) api.get("/wallet/tips").then(({ data }) => setTips(data));
    if (tab === "withdraw" && !withdrawals) api.get("/wallet/withdrawals").then(({ data }) => setWithdrawals(data));
  }, [tab]); // eslint-disable-line

  const buyCoins = async (packId) => {
    setBusy(true);
    try { const { data } = await api.post("/payments/coins/checkout", { pack_id: packId, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errText(e)); setBusy(false); }
  };

  const exportStatement = async () => {
    try {
      const res = await api.get(`/wallet/statement?month=${stmtMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `virtustars-statement-${stmtMonth}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) { toast.error(errText(e)); }
  };

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < minVc) return toast.error(`Minimum withdrawal is ${minVc} VC`);
    if (amt > (w?.withdrawable || 0)) return toast.error(`You can only withdraw earned VC. Withdrawable: ${vc(w?.withdrawable || 0)} VC`);
    if (!destination.trim()) return toast.error("Enter your payout destination");
    setBusy(true);
    try { await api.post("/wallet/withdraw", { amount: amt, method, destination }); toast.success("Withdrawal requested — pending review"); setAmount(""); setDestination(""); loadWallet(); api.get("/wallet/withdrawals").then(({ data }) => setWithdrawals(data)); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!w) return <Loading />;
  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center gap-2 mb-5"><WalletIcon className="text-gold" /><h1 className="font-display font-bold text-2xl">VWallet</h1></div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Balance icon={Coins} label="Available" value={w.available} primary />
        <Balance icon={ShieldCheck} label="Withdrawable" value={w.withdrawable} />
        <Balance icon={Clock} label="Pending" value={w.pending} />
        <Balance icon={TrendingUp} label="Lifetime Earned" value={w.lifetime} />
        <Balance icon={Gift} label="Tips Received" value={w.tips_received} />
      </div>
      {w.promo > 0 && <div className="surface rounded-xl px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-2 border border-gold/30" data-testid="wallet-promo">
        <Coins size={15} className="text-gold" /><span className="font-semibold text-gold">{Number(w.promo).toFixed(2)} promo VC</span>
        <span className="text-muted-foreground text-xs">from your VirtuCity bank. Spent first on tips and Marketplace buys; can't be cashed out.</span></div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="surface-2 mb-4 flex-wrap h-auto">
          {["transactions", "rewards", "tips", "buy", "withdraw"].map((t) => <TabsTrigger key={t} value={t} data-testid={`wallet-tab-${t}`} className="capitalize data-[state=active]:bg-white/10 data-[state=active]:text-eblue">{t === "buy" ? "Buy VC" : t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="buy">
          <div className="surface rounded-2xl p-5">
            <div className="font-display font-semibold mb-1 flex items-center gap-2"><Plus size={17} className="text-gold" /> Buy VirtuCoins</div>
            <p className="text-xs text-muted-foreground mb-4">Top up to tip creators, unlock content and support the community. Purchased VirtuCoins are spendable but not withdrawable.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {packs.map((p) => (
                <div key={p.id} className="surface-2 rounded-2xl p-4 text-center border border-white/5 hover:border-gold/40 transition-colors" data-testid={`coin-pack-${p.id}`}>
                  {p.bonus > 0 && <div className="text-[10px] text-gold font-semibold mb-1">+{p.bonus} BONUS</div>}
                  <Coins size={22} className="text-gold mx-auto" />
                  <div className="font-display font-bold text-2xl mt-2">{p.vc}</div>
                  <div className="text-[11px] text-muted-foreground mb-3">VirtuCoins · {p.label}</div>
                  <Button onClick={() => buyCoins(p.id)} disabled={busy} className="btn-gold w-full rounded-xl h-9 text-sm" data-testid={`buy-${p.id}`}>${p.usd}</Button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-3">Secure checkout via Stripe. Test card 4242 4242 4242 4242, any future date & CVC.</p>
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          {!txns ? <Loading /> : txns.length === 0 ? <Empty icon={Coins} title="No transactions yet" hint="Earn VirtuCoins from likes, streams and tips." /> : (
            <div className="surface rounded-2xl divide-y divide-white/5">{txns.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3.5" data-testid={`txn-${t.id}`}>
                <div><div className="text-sm font-medium capitalize">{t.type.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()} · {t.status}</div></div>
                <div className={`font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.amount >= 0 ? "+" : ""}{vc(t.amount)} VC</div>
              </div>
            ))}</div>
          )}
        </TabsContent>

        <TabsContent value="rewards">
          {!rewards ? <Loading /> : rewards.length === 0 ? <Empty icon={Gift} title="No reward events yet" /> : (
            <div className="surface rounded-2xl divide-y divide-white/5">{rewards.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3.5">
                <div><div className="text-sm font-medium capitalize">Qualified {r.action.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div></div>
                <div className="flex items-center gap-3"><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : r.status === "flagged" ? "bg-amber-500/15 text-amber-400" : "bg-white/10 text-muted-foreground"}`}>{r.status}</span><span className="font-mono text-gold">+{vc(r.amount)}</span></div>
              </div>
            ))}</div>
          )}
        </TabsContent>

        <TabsContent value="tips">
          {!tips ? <Loading /> : tips.length === 0 ? <Empty icon={Gift} title="No tips yet" /> : (
            <div className="surface rounded-2xl divide-y divide-white/5">{tips.map((t) => {
              const sent = t.from_id === user.id; const other = sent ? t.to : t.from;
              return <div key={t.id} className="flex items-center gap-3 p-3.5"><Avatar src={other?.avatar_url} name={other?.display_name} size={36} /><div className="flex-1"><div className="text-sm">{sent ? "Sent to" : "From"} <span className="font-medium">{other?.display_name}</span></div><div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</div></div><div className={`font-mono ${sent ? "text-rose-400" : "text-emerald-400"}`}>{sent ? "-" : "+"}{vc(t.amount)} VC</div></div>;
            })}</div>
          )}
        </TabsContent>

        <TabsContent value="withdraw">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="surface rounded-2xl p-5">
              <div className="font-display font-semibold mb-1">Withdraw earnings to USD</div>
              {!w.can_withdraw ? (
                <div className="mt-2 rounded-xl border border-gold/25 bg-gradient-to-br from-gold/10 to-transparent p-4" data-testid="withdraw-gate">
                  <div className="flex items-center gap-2 text-gold text-[11px] font-semibold tracking-widest uppercase mb-1"><Star size={13} /> Monetization locked</div>
                  <p className="text-sm text-muted-foreground">Withdrawals unlock with <span className="text-white font-medium">VirtuStars+</span> or once you reach <span className="text-white font-medium">10,000 followers</span>.</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Follower progress</span><span className="text-gold">{formatNum(user?.follower_count || 0)} / 10,000</span></div>
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${Math.min(100, ((user?.follower_count || 0) / 10000) * 100)}%` }} /></div>
                  </div>
                  <a href="/plus" className="btn-gold inline-flex items-center justify-center rounded-full h-11 px-5 mt-4 font-semibold text-sm w-full" data-testid="withdraw-upgrade-btn">Upgrade to VirtuStars+ to monetize</a>
                </div>
              ) : (<>
              <p className="text-xs text-muted-foreground mb-3">1 VC = $1.00 · 5% fee · Min {minVc} VC. Only <span className="text-gold font-medium">earned</span> VirtuCoins are withdrawable — withdrawable now: <span className="text-gold font-mono">{vc(w.withdrawable)} VC</span>.</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <select value={method} onChange={(e) => setMethod(e.target.value)} className="surface-2 border border-white/10 rounded-xl px-3 h-11 text-sm" data-testid="withdraw-method">
                  <option value="PayPal">PayPal</option><option value="Bank">Bank transfer</option>
                </select>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount (min ${minVc})`} className="surface-2 border-white/10" data-testid="withdraw-amount-input" />
              </div>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={method === "PayPal" ? "PayPal email" : "Bank account / IBAN"} className="surface-2 border-white/10 mb-3" data-testid="withdraw-destination" />
              {amount && parseFloat(amount) > 0 && (
                <div className="text-xs space-y-1 mb-3 surface-2 rounded-lg p-3">
                  <Row l="Amount" v={`${vc(amount)} VC`} /><Row l="Fee (5%)" v={`-${vc(parseFloat(amount) * 0.05)} VC`} /><Row l="You receive" v={`$${vc(parseFloat(amount) * 0.95)}`} bold /><Row l="Processing" v="2–5 business days" />
                </div>
              )}
              <Button onClick={withdraw} disabled={busy} className="btn-gold w-full rounded-xl h-11" data-testid="withdraw-btn"><ArrowDownToLine size={16} className="mr-1" />{busy ? "Requesting…" : "Request Withdrawal"}</Button>
              <p className="text-[11px] text-muted-foreground/70 mt-2">Purchased VirtuCoins cannot be withdrawn. Identity/payment verification may be required.</p>
              </>)}
              {isPlus && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><Star size={13} className="text-gold" /> Earnings statement <span className="text-[10px] text-gold">VirtuStars+</span></div>
                  <div className="flex gap-2">
                    <Input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} className="surface-2 border-white/10 h-10" data-testid="statement-month" />
                    <Button onClick={exportStatement} variant="outline" className="rounded-xl h-10 border-white/15" data-testid="export-statement-btn"><Download size={15} className="mr-1" />Export CSV</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="surface rounded-2xl p-5">
              <div className="font-display font-semibold mb-3">Withdrawal history</div>
              {!withdrawals ? <Loading /> : withdrawals.length === 0 ? <p className="text-sm text-muted-foreground">No withdrawals yet.</p> : (
                <div className="space-y-2">{withdrawals.map((wd) => <div key={wd.id} className="flex justify-between items-center text-sm surface-2 rounded-lg p-3"><div><div className="font-mono">{vc(wd.amount)} VC → ${vc(wd.net_usd)}</div><div className="text-xs text-muted-foreground">{new Date(wd.created_at).toLocaleDateString()}</div></div><span className="text-xs px-2 py-0.5 rounded-full bg-white/10">{wd.status.replace(/_/g, " ")}</span></div>)}</div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Balance({ icon: Icon, label, value, primary }) {
  return <div className={`rounded-2xl p-4 ${primary ? "glass glass-gold" : "surface"}`}><Icon size={18} className={primary ? "text-gold" : "text-muted-foreground"} /><div className={`font-mono text-2xl mt-2 ${primary ? "text-gold" : ""}`}>{vc(value)}</div><div className="text-xs text-muted-foreground">{label} VC</div></div>;
}
function Row({ l, v, bold }) { return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={bold ? "text-gold font-semibold" : ""}>{v}</span></div>; }
